import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Star, Pencil, Trash2, FileText, MapPin, Users, Tag } from 'lucide-react';
import { TemplateFormSheet } from '@/components/template-form-sheet';
import type { ProjectTemplate as FormProjectTemplate, CreateTemplateData } from '@/types/template.types';
import { useTemplates, type ProjectTemplate } from '@/contexts/TemplateContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Helper to convert TemplateContext's ProjectTemplate to the form's ProjectTemplate type
const toFormTemplate = (template: ProjectTemplate): FormProjectTemplate => ({
  id: template.id,
  templateName: template.name,
  templateDescription: template.templateDescription,
  isFavorite: template.isFavorite,
  createdAt: template.createdAt.toISOString(),
  updatedAt: template.updatedAt.toISOString(),
  description: template.description,
  priority: template.priority,
  type: template.type,
  location: template.location,
  unit: template.unit as FormProjectTemplate['unit'],
  currencyCode: template.currencyCode,
  clients: template.clients,
  tags: template.tags,
});

export function SettingsTemplates() {
  const { templates, addTemplate, updateTemplate, deleteTemplate, toggleFavorite } = useTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ProjectTemplate | null>(null);

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setSheetMode('create');
    setSheetOpen(true);
  };

  // Listen for create-template event from the header bar button
  useEffect(() => {
    const handleCreateEvent = () => handleCreateTemplate();
    window.addEventListener('create-template', handleCreateEvent);
    return () => window.removeEventListener('create-template', handleCreateEvent);
  }, []);

  const handleEditTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setSheetMode('edit');
    setSheetOpen(true);
  };

  const handleDeleteClick = (template: ProjectTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveTemplate = (data: CreateTemplateData) => {
    if (sheetMode === 'create') {
      addTemplate({
        name: data.templateName,
        templateDescription: data.templateDescription,
        description: data.description,
        priority: data.priority,
        type: data.type,
        location: data.location,
        unit: data.unit,
        currencyCode: data.currencyCode,
        clients: data.clients,
        tags: data.tags,
        isFavorite: false,
      });
    } else if (selectedTemplate) {
      updateTemplate(selectedTemplate.id, {
        name: data.templateName,
        templateDescription: data.templateDescription,
        description: data.description,
        priority: data.priority,
        type: data.type,
        location: data.location,
        unit: data.unit,
        currencyCode: data.currencyCode,
        clients: data.clients,
        tags: data.tags,
      });
    }
  };

  const handleToggleFavorite = (templateId: string) => {
    toggleFavorite(templateId);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
            <p className="text-muted-foreground max-w-md mb-4">
              Create your first template to start streamlining project creation with pre-filled fields.
            </p>
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card
              key={template.id}
              className="group hover:shadow-md transition-shadow cursor-pointer relative"
              onClick={() => handleEditTemplate(template)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{template.name}</CardTitle>
                    {template.templateDescription && (
                      <CardDescription className="line-clamp-2 mt-1">
                        {template.templateDescription}
                      </CardDescription>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-8 w-8 shrink-0',
                      template.isFavorite && 'text-yellow-500'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(template.id);
                    }}
                  >
                    <Star
                      className={cn('h-4 w-4', template.isFavorite && 'fill-current')}
                    />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Template Details */}
                <div className="space-y-2 text-sm">
                  {template.priority && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {template.priority}
                      </Badge>
                      {template.type && (
                        <Badge variant="secondary" className="text-xs">
                          {template.type}
                        </Badge>
                      )}
                    </div>
                  )}

                  {template.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{template.location}</span>
                    </div>
                  )}

                  {template.clients && template.clients.length > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {template.clients.length} client{template.clients.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {template.tags && template.tags.length > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Tag className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {template.tags.length} tag{template.tags.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditTemplate(template);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(template);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Template Form Sheet */}
      <TemplateFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        mode={sheetMode}
        template={selectedTemplate ? toFormTemplate(selectedTemplate) : undefined}
        onSave={handleSaveTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
